/**
 * Change how many seats a team buys on top of its plan's bundle ($12 per user
 * on Starter and Team; Emily, 2026-10-07). The server (routes/billing.ts
 * POST /extra-seats) changes the Stripe subscription and answers with the new
 * cap.
 *
 * Every outcome resolves to data, never a throw, so the control can always
 * say something true. Pure on purpose, like stripeRedirect.ts: no logger and
 * no apiBaseUrl import, so it is tested for real and the hook supplies both.
 */
export type ExtraSeatsResult =
  | { ok: true; extraSeats: number; maxSeats: number }
  | { ok: false; code: string; message?: string };

export async function saveExtraSeats(opts: {
  apiBase: string;
  teamId: string | undefined;
  getToken: () => Promise<string | null>;
  extraSeats: number;
}): Promise<ExtraSeatsResult> {
  const { apiBase, teamId, getToken, extraSeats } = opts;
  if (!teamId) return { ok: false, code: 'NO_TEAM' };

  let res: Response;
  try {
    const token = await getToken();
    res = await fetch(`${apiBase}/api/billing/extra-seats`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ teamId, extraSeats }),
    });
  } catch {
    return { ok: false, code: 'NETWORK' };
  }

  const json = (await res.json().catch(() => null)) as
    | { data?: { extraSeats?: unknown; maxSeats?: unknown }; error?: { code?: unknown; message?: unknown } }
    | null;

  if (!res.ok) {
    const code = json?.error?.code;
    const message = json?.error?.message;
    return {
      ok: false,
      code: typeof code === 'string' && code ? code : 'BAD_RESPONSE',
      ...(typeof message === 'string' && message ? { message } : {}),
    };
  }

  const saved = json?.data?.extraSeats;
  const maxSeats = json?.data?.maxSeats;
  if (typeof saved !== 'number' || typeof maxSeats !== 'number') return { ok: false, code: 'BAD_RESPONSE' };
  return { ok: true, extraSeats: saved, maxSeats };
}

const RETRY_COPY = "We couldn't update your seats just now. Please try again in a moment.";

/**
 * The line under the control after a refusal. A count below the team uses the
 * server's own sentence, which names the member count; the rest is fixed copy
 * so a provider's internal message never reaches the page.
 */
export function extraSeatsErrorCopy(code: string, serverMessage?: string): string {
  switch (code) {
    case 'SEATS_BELOW_MEMBERS':
      return serverMessage || 'Your team has more members than that. Remove a member first.';
    case 'NOT_OWNER':
      return 'Only the team owner can change seats.';
    case 'PAYMENT_INCOMPLETE':
      return 'Payment did not complete, so your seats have not changed. Update your payment method and try again.';
    default:
      return RETRY_COPY;
  }
}
